import { signal } from '@angular/core';

/**
 * Grace period granted to the pointer after it leaves an option, so a diagonal move
 * towards an entry further down the submenu does not collapse it mid-travel.
 */
export const SUBMENU_CLOSE_DELAY_MS = 300;

/**
 * Open/pinned state of the submenu of a single menu level.
 *
 * Pinning exists because hover alone is too fragile to reach a submenu: a click on a
 * parent option locks its submenu open until the user explicitly dismisses it.
 */
export class SubmenuState {
  readonly openIndex = signal<number | null>(null);
  readonly pinnedIndex = signal<number | null>(null);

  private closeTimer?: ReturnType<typeof setTimeout>;

  hoverEnter(index: number, optionHasSubOptions: boolean): void {
    this.cancelPendingClose();

    const pinned = this.pinnedIndex();
    if (pinned === null) {
      this.openIndex.set(index);
      return;
    }
    if (pinned === index) {
      return;
    }

    if (optionHasSubOptions) {
      this.pinnedIndex.set(index);
      this.openIndex.set(index);
    }
  }

  hoverLeave(): void {
    if (this.pinnedIndex() !== null) {
      return;
    }

    this.cancelPendingClose();
    this.closeTimer = setTimeout(() => {
      this.closeTimer = undefined;
      this.openIndex.set(null);
    }, SUBMENU_CLOSE_DELAY_MS);
  }

  togglePin(index: number): void {
    this.cancelPendingClose();

    if (this.pinnedIndex() === index) {
      this.pinnedIndex.set(null);
      this.openIndex.set(null);
      return;
    }

    this.pinnedIndex.set(index);
    this.openIndex.set(index);
  }

  reset(): void {
    this.cancelPendingClose();
    this.pinnedIndex.set(null);
    this.openIndex.set(null);
  }

  private cancelPendingClose(): void {
    if (this.closeTimer !== undefined) {
      clearTimeout(this.closeTimer);
      this.closeTimer = undefined;
    }
  }
}
